import { useCustomerProfile } from '@/hooks/useCustomerProfile';

/**
 * The company a conversation is on behalf of, for surfaces that need ONLY the company and not the
 * whole customer profile (CRM v2 WS-6.2 — currently the thread header).
 *
 * <b>A projection over {@link useCustomerProfile}, not its own fetch.</b> It used to fetch
 * independently because `ChatView` and the customer panel were siblings with no shared state; the
 * profile fetch now lives in one hook, so this is a pure derivation over it. The exported shape is
 * unchanged on purpose — `ChatView` and its tests are untouched by the refactor, which is what
 * makes the change provably behaviour-preserving.
 *
 * Both invariants still hold, now enforced in one place instead of two:
 *  - <b>Anonymous visitors are never looked up</b> — zero network calls.
 *  - <b>Fail-soft</b> — a rejected lookup yields no company, never a thrown error or a visible
 *    message. The company is decoration on a header; it must not be able to break the thread.
 */
export interface CustomerCompany {
  /** Resolved Company record name, else the contact's free-text company. Empty when neither exists. */
  name: string;
  /** True when `name` came from an actual CRM Company RECORD rather than a typed-in contact field. */
  fromRecord: boolean;
}

const NONE: CustomerCompany = { name: '', fromRecord: false };

export function useCustomerCompany(customerId: string | undefined | null): CustomerCompany {
  const { profile } = useCustomerProfile(customerId);

  // Precedence: a resolved Company RECORD first, then the contact's free-text company field. Both
  // read as "the company" to an agent, but only the first came from a record — the second is a
  // string someone once typed and may name a company that does not exist, or spell one five ways.
  const resolved = (profile?.companyName ?? '').trim();
  if (resolved) return { name: resolved, fromRecord: true };

  const freeText = (profile?.company ?? '').trim();
  if (freeText) return { name: freeText, fromRecord: false };

  return NONE;
}
